import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Loader2, Minus, Plus, Search, Settings, Shield, Ticket, Users } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AdminPayments } from '@/components/admin/AdminPayments';
import { AdminCoupons } from '@/components/admin/AdminCoupons';
import { AdminSettings } from '@/components/admin/AdminSettings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const TABS = [
  { id: 'users', label: 'Kullanıcılar', icon: Users },
  { id: 'payments', label: 'Ödeme Talepleri', icon: CreditCard },
  { id: 'coupons', label: 'Hak Kodları', icon: Ticket },
  { id: 'settings', label: 'Ayarlar', icon: Settings },
];

export default function Admin() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [delta, setDelta] = useState(10);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([
        api.get('/admin/users', { params: search ? { search } : {} }),
        api.get('/admin/generations', { params: { limit: 12 } }),
      ]);
      setUsers(u.data.users);
      setRecent(g.data.generations);
      await loadStats();
    } catch (e) {
      toast.error(errMsg(e, 'Admin verileri yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [search, loadStats]);

  useEffect(() => {
    load();
  }, [load]);

  const adjust = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/credits`, {
        delta: Number(delta),
        note,
      });
      setUsers((p) => p.map((u) => (u.id === data.user.id ? { ...u, credits: data.user.credits } : u)));
      setTarget(null);
      setNote('');
      toast.success('Hak güncellendi');
    } catch (e) {
      toast.error(errMsg(e, 'Güncellenemedi'));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = async (u) => {
    try {
      const role = u.role === 'admin' ? 'user' : 'admin';
      const { data } = await api.post(`/admin/users/${u.id}/role`, { role });
      setUsers((p) => p.map((x) => (x.id === data.user.id ? { ...x, role: data.user.role } : x)));
      toast.success(`Rol güncellendi: ${role}`);
    } catch (e) {
      toast.error(errMsg(e, 'Rol güncellenemedi'));
    }
  };

  const statCards = [
    { label: 'Kullanıcı', value: stats?.total_users ?? 0 },
    { label: 'Bekleyen Ödeme', value: stats?.pending_payments ?? 0, alert: (stats?.pending_payments ?? 0) > 0 },
    { label: 'Ciro (TL)', value: stats?.revenue ?? 0 },
    { label: 'Satılan Hak', value: stats?.credits_sold ?? 0 },
    { label: 'Üretim', value: stats?.total_generations ?? 0 },
    { label: 'Başarısız', value: stats?.failed_generations ?? 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight">
            <Shield className="h-5 w-5 text-[hsl(var(--brand-red))]" /> Admin Panel
          </h1>
          <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">
            Kullanıcıları, ödemeleri ve kapak hakkı kodlarını yönet.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {statCards.map((s) => (
          <div
            key={s.label}
            className={[
              'panel p-4',
              s.alert ? 'border-[hsl(var(--brand-red))]' : '',
            ].join(' ')}
          >
            <p className="text-xs font-medium text-[hsl(var(--text-muted))]">{s.label}</p>
            <p
              className={[
                'mt-2 font-display text-2xl font-semibold',
                s.alert ? 'text-[hsl(var(--brand-red))]' : '',
              ].join(' ')}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div
        data-testid="admin-tabs"
        className="flex items-center gap-1 overflow-x-auto rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-1"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={[
              'flex shrink-0 items-center gap-2 rounded-[9px] px-3.5 py-2 text-xs font-semibold transition-colors duration-150',
              tab === t.id
                ? 'bg-[hsl(var(--brand-red))] text-white'
                : 'text-[hsl(var(--text-muted))] hover:text-white',
            ].join(' ')}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
            {t.id === 'payments' && (stats?.pending_payments ?? 0) > 0 && tab !== 'payments' && (
              <span className="ml-1 grid h-4 min-w-4 place-items-center rounded-full bg-[hsl(var(--brand-red))] px-1 text-[10px] font-bold text-white">
                {stats.pending_payments}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <div className="space-y-5">
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <Input
              data-testid="admin-user-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="E-posta veya isim ara"
              className="bg-[hsl(var(--surface-2))] pl-9"
            />
          </div>

          <section className="panel overflow-hidden">
            <div className="flex items-center gap-2 border-b border-[hsl(var(--border-soft))] px-4 py-3">
              <Users className="h-4 w-4 text-[hsl(var(--brand-red))]" />
              <h2 className="font-display text-sm font-semibold">Kullanıcılar</h2>
            </div>

            {loading ? (
              <div className="grid place-items-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-red))]" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="admin-users-table">
                  <TableHeader>
                    <TableRow className="border-[hsl(var(--border-soft))] hover:bg-transparent">
                      <TableHead className="text-[hsl(var(--text-muted))]">Kullanıcı</TableHead>
                      <TableHead className="text-[hsl(var(--text-muted))]">E-posta</TableHead>
                      <TableHead className="text-[hsl(var(--text-muted))]">Hak</TableHead>
                      <TableHead className="text-[hsl(var(--text-muted))]">Üretim</TableHead>
                      <TableHead className="text-[hsl(var(--text-muted))]">Rol</TableHead>
                      <TableHead className="text-right text-[hsl(var(--text-muted))]">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-[hsl(var(--border-soft))]">
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-[hsl(var(--text-muted))]">{u.email}</TableCell>
                        <TableCell className="font-mono-alt font-semibold text-[hsl(var(--brand-red))]">
                          {u.credits}
                        </TableCell>
                        <TableCell className="font-mono-alt">{u.generation_count}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            data-testid={`admin-role-toggle-${u.id}`}
                            onClick={() => toggleRole(u)}
                            className={[
                              'rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors duration-150',
                              u.role === 'admin'
                                ? 'bg-[hsl(var(--brand-red))] text-white'
                                : 'bg-[hsl(var(--surface-2))] text-[hsl(var(--text-2))] hover:bg-[hsl(var(--surface))]',
                            ].join(' ')}
                          >
                            {u.role}
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            data-testid={`admin-adjust-credits-button-${u.id}`}
                            onClick={() => {
                              setTarget(u);
                              setDelta(10);
                            }}
                            className="h-8 bg-[hsl(var(--brand-red))] text-xs font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
                          >
                            Hak Ayarla
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          <section className="panel p-5">
            <h2 className="font-display text-sm font-semibold">Son Üretimler</h2>
            {recent.length === 0 ? (
              <p className="mt-3 text-sm text-[hsl(var(--text-muted))]">Henüz üretim yok.</p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                {recent.map((g) => (
                  <div
                    key={g.id}
                    className="overflow-hidden rounded-[10px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
                  >
                    <div style={{ aspectRatio: g.orientation === 'portrait' ? '9/16' : '16/9' }}>
                      {g.thumb_url ? (
                        <img src={fileUrl(g.thumb_url)} alt="Kapak" className="h-full w-full object-cover" />
                      ) : (
                        <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-[hsl(var(--text-muted))]">
                          {g.status}
                        </div>
                      )}
                    </div>
                    <p className="truncate px-2 py-1.5 text-[10px] text-[hsl(var(--text-muted))]">
                      {g.user_email}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {tab === 'payments' && (
        <AdminPayments
          onChanged={() => {
            loadStats();
            load();
          }}
        />
      )}
      {tab === 'coupons' && <AdminCoupons />}
      {tab === 'settings' && <AdminSettings />}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Hak Ayarla</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-muted))]">
              {target?.email} - mevcut bakiye: {target?.credits}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Değişim miktarı (negatif değer düşürür)</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  data-testid="admin-delta-minus"
                  onClick={() => setDelta((d) => Number(d) - 10)}
                  className="h-10 w-10 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  data-testid="admin-delta-input"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  className="bg-[hsl(var(--surface-2))] text-center font-mono-alt"
                />
                <Button
                  variant="secondary"
                  size="icon"
                  data-testid="admin-delta-plus"
                  onClick={() => setDelta((d) => Number(d) + 10)}
                  className="h-10 w-10 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Not (opsiyonel)</Label>
              <Input
                data-testid="admin-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Örn: manuel ödeme karşılığı"
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={adjust}
              disabled={saving}
              data-testid="admin-adjust-confirm-button"
              className="h-11 w-full bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
